import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const ReactJsCourse = () => {
  const testimonials = [
    {
      id: 1,
      name: "Akash Verma",
      review:
        "The ReactJS training was incredibly thorough. I learned to build robust single-page applications and felt prepared for my new role as a front-end developer. Best ReactJS course in Noida!",
      rating: 5,
      date: "2024-06-10",
      profileImg: "https://randomuser.me/api/portraits/men/34.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Priya Das",
      review:
        "I came with basic JavaScript knowledge and left with confidence in React. The instructors explain complex topics like Hooks and Redux beautifully. The practical projects were the highlight.",
      rating: 4,
      date: "2024-06-08",
      profileImg: "https://randomuser.me/api/portraits/women/47.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Manish Joshi",
      review:
        "From component lifecycle to state management, every aspect of React was covered in depth. The real-time project at the end truly solidified my understanding. Highly recommend this course!",
      rating: 5,
      date: "2024-06-05",
      profileImg: "https://randomuser.me/api/portraits/men/52.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Sneha Sharma",
      review:
        "The curriculum is very up-to-date, covering modern React features like Hooks and Context API. Plus, the placement assistance was top-notch. I got placed in a great company!",
      rating: 5,
      date: "2024-06-03",
      profileImg: "https://randomuser.me/api/portraits/women/62.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Gaurav Saini",
      review:
        "Fantastic course for anyone serious about front-end development. The hands-on practice, coding challenges, and expert guidance made learning React enjoyable and effective.",
      rating: 4,
      date: "2024-06-01",
      profileImg: "https://randomuser.me/api/portraits/men/77.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is ReactJS?",
      answer:
        "ReactJS is a popular open-source JavaScript library developed by Facebook for building user interfaces, especially single-page applications. It allows developers to create reusable UI components, making development faster and more efficient, and focuses on the 'view' layer of web and mobile apps.",
    },
    {
      title: "What are the prerequisites for this ReactJS course?",
      answer:
        "A strong understanding of HTML, CSS, and **modern JavaScript (ES6+)** is essential. Familiarity with basic command-line operations is also helpful. Our course builds upon these fundamentals to teach you ReactJS from scratch.",
    },
    {
      title: "What key concepts will I learn in this ReactJS course?",
      answer:
        "You will learn core React concepts like components (functional and class-based), JSX, props, state, lifecycle methods, **React Hooks (useState, useEffect, useContext)**, routing with React Router, state management with **Redux/Context API**, API integration, and basic performance optimization.",
    },
    {
      title: "Is ReactJS still in demand in the industry?",
      answer:
        "Absolutely! ReactJS is one of the most in-demand front-end technologies globally. Companies of all sizes, from startups to tech giants, heavily rely on React for building their user interfaces, ensuring high job opportunities.",
    },
    {
      title: "Will I get placement assistance after course completion?",
      answer:
        "Yes, we provide 100% placement assistance for our ReactJS graduates. This includes professional resume building, intensive mock interviews, soft skills training, and connecting you with our network of hiring companies for Front-End Developer and React Developer roles in Noida and pan-India.",
    },
    {
      title: "What kind of projects will I work on during the training?",
      answer:
        "You will develop several hands-on projects, including a major Capstone Project. These projects will involve building real-world applications like an e-commerce storefront, a social media feed, or a task management system, which you can add to your professional portfolio.",
    },
    {
      title: "What are the career opportunities after this ReactJS training?",
      answer:
        "Upon successful completion, you can pursue roles such as React Developer, Front-End Developer, UI Developer, Web Developer (React), and Software Engineer focusing on client-side development.",
    },
    {
      title: "What is the duration and mode of training?",
      answer:
        "The course duration varies based on the pace and mode (e.g., intensive fast-track vs. regular batch). We offer both immersive offline classroom training at our Noida center and interactive live online classes, providing flexibility to suit your learning preference.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: JavaScript (ES6+) Refresher",
      description: "Arrow Functions, let/const, Classes, Modules, Destructuring, Spread/Rest Operators, Promises, Async/Await - essential for modern React.",
    },
    {
      title: "Module 2: Introduction to React & JSX",
      description: "Why React? Setting up a React project (Create React App), understanding components, virtual DOM, and writing declarative UI with JSX.",
    },
    {
      title: "Module 3: React Components: Props & State",
      description: "Functional vs. Class Components, understanding Props for passing data, managing dynamic data with State, and event handling.",
    },
    {
      title: "Module 4: React Hooks (useState, useEffect, useContext)",
      description: "In-depth coverage of React Hooks: `useState` for state management, `useEffect` for side effects, `useContext` for global state, and other common hooks.",
    },
    {
      title: "Module 5: Component Lifecycle & Advanced Hooks",
      description: "Understanding component lifecycle in functional components with `useEffect`, and exploring `useReducer`, `useRef`, `useCallback`, `useMemo` for performance.",
    },
    {
      title: "Module 6: React Router for Navigation",
      description: "Implementing client-side routing, nested routes, programmatic navigation, and protected routes using React Router DOM.",
    },
    {
      title: "Module 7: State Management (Context API & Redux Basics)",
      description: "Managing global state with React's Context API. Introduction to Redux for larger applications (Redux core principles, actions, reducers, store).",
    },
    {
      title: "Module 8: Working with APIs & Forms",
      description: "Fetching data from RESTful APIs using `fetch` or Axios, conditional rendering based on API data, and building controlled forms with validation.",
    },
    {
      title: "Module 9: Styling in React & UI Libraries",
      description: "Different styling approaches (CSS Modules, Styled Components), integrating popular UI libraries like Material-UI or Ant Design, and responsive design.",
    },
    {
      title: "Module 10: Performance Optimization & Testing",
      description: "Techniques for optimizing React app performance (`React.memo`, lazy loading). Introduction to unit testing with Jest and React Testing Library.",
    },
    {
      title: "Module 11: Deployment & Best Practices",
      description: "Preparing a React app for production, deploying to platforms like Netlify/Vercel, and industry best practices for writing clean, scalable React code.",
    },
    {
      title: "Module 12: Capstone Project",
      description: "Build a comprehensive, real-world React application from scratch, incorporating all learned concepts. This project will be a centerpiece of your professional portfolio.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>ReactJS Course in Noida | Master Front-End Development</title>
        <meta
          name="description"
          content="Master ReactJS development in Noida. Our comprehensive training covers Hooks, Redux, Routing, and building dynamic user interfaces with 100% placement assistance."
        />
        <meta
          name="keywords"
          content="ReactJS training Noida, React course, learn React, Front-End Development, React Hooks, Redux, React Router, UI Development, JavaScript framework, ReactJS certification, Web Development"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://www.getaprogrammer.co/wp-content/uploads/2020/04/react.jpg"
          alt="ReactJS Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Master <span className="text-blue-400">ReactJS</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Become a skilled Front-End Developer building modern, dynamic user interfaces with the most popular JavaScript library.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-blue-700 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
             <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("React Js Course")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn ReactJS Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Learn ReactJS?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            ReactJS is the go-to library for building fast, scalable, and interactive user interfaces. Its component-based architecture and vast community support make it an indispensable skill for modern web developers.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">Industry Standard</h3>
              <p className="text-gray-600">Widely adopted by tech giants and startups alike for UI development.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">High Demand</h3>
              <p className="text-gray-600">Consistent demand for skilled React developers globally.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">Rich Ecosystem</h3>
              <p className="text-gray-600">Leverage a vast array of tools, libraries, and community support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🚀', title: 'Hands-on Project Based Learning', desc: 'Build multiple real-world applications to solidify your React skills.' },
              { icon: '💡', title: 'Master Modern React Hooks', desc: 'Deep dive into `useState`, `useEffect`, `useContext`, and custom hooks.' },
              { icon: '👨‍🏫', title: 'Expert-Led Training', desc: 'Learn from seasoned React developers with extensive industry experience.' },
              { icon: '🎓', title: 'Industry-Recognized Certification', desc: 'Receive a prestigious certificate validating your ReactJS expertise.' },
              { icon: '🤝', title: 'Dedicated Placement Support', desc: 'Comprehensive assistance for resume building, mock interviews, and job placements.' },
              { icon: '🔄', title: 'Flexible Learning Modes', desc: 'Choose between dynamic offline classroom sessions or interactive online live classes.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-blue-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-blue-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Technologies You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our ReactJS training ensures you gain proficiency in all essential tools and technologies for modern front-end development.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'ReactJS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
              { name: 'JavaScript (ES6+)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
              { name: 'HTML5', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
              { name: 'CSS3', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
              { name: 'Redux', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg' },
              { name: 'Node.js (NPM/Yarn)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
              { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
              { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
              { name: 'Jest', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg' },
              { name: 'NPM', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/npm/npm-original-wordmark.svg' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A ReactJS certification from us will prepare you for highly sought-after front-end development roles in the tech industry.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '💻', title: 'React Developer', desc: 'Specialize in building and maintaining user interfaces using the ReactJS library.' },
              { icon: '🌐', title: 'Front-End Developer', desc: 'Focus on implementing user interfaces and client-side logic for web applications.' },
              { icon: '✨', title: 'UI Developer', desc: 'Translate UI/UX designs into interactive and responsive web components using React.' },
              { icon: '🛠️', title: 'Web Application Developer', desc: 'Develop, test, and deploy robust web applications with a strong React focus.' },
              { icon: '📈', title: 'Full Stack Developer (with React)', desc: 'Combine your React skills with backend technologies to build complete applications.' },
              { icon: '💡', title: 'Freelance React Developer', desc: 'Offer your React expertise to diverse clients as an independent contractor.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-blue-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our ReactJS training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., ReactJS Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-900">
            What Our ReactJS Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-blue-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-blue-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-blue-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>
      {/* Conditionally render the popup */}
            {isPopupOpen && (
              <JoinNowFormPopup
                onClose={handleClosePopup}
                courseTitle={selectedCourseTitle}
              />
            )}
    </div>
  );
};

export default ReactJsCourse;